"use client";

import { usePostHog } from "posthog-js/react";
import type { ReactNode } from "react";

/** Where a support request came from — one value per placement. */
export type SupportSurface =
	| "contact_page"
	| "employers_zone"
	// B2B inbound-contact CTA placements (WO-4a, 2026-09-22): a "doesn't fit /
	// partnership idea" entry point distinct from the existing `employers_zone`
	// shortlist-page usage, so the two intents never collapse into one number.
	| "footer"
	| "advertise"
	| "employers_partnership";

const SUPPORT_EMAIL = "hello@lokerdollar.com";

const FOCUS_RING =
	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/**
 * The widget's own root node — an **id**, not a class.
 *
 * `conversations.js` creates and finds it by id, and never sets a className:
 *
 *   se.getElementById(yt)
 *   (n = se.createElement("div")).id = yt
 *
 * The first version of this file queried `.ph-conversations-widget-container`
 * and therefore matched nothing in production, which is how the button stayed
 * silently dead through a fix that was supposed to revive it. Looked up with
 * `getElementById` to mirror exactly what the extension does.
 *
 * It is a normal light-DOM node — no shadow root anywhere in the extension —
 * so it is reachable from here.
 */
export const WIDGET_CONTAINER_ID = "ph-conversations-widget-container";

/**
 * Opens the PostHog chat panel, returning whether it actually managed to.
 *
 * `posthog.conversations.show()` does NOT do this, which is the bug this
 * function exists to work around. From the shipped extension:
 *
 *   show(){ this.Vf ? this.Cf || this.Qf() : warn("…domain is not allowed") }
 *   isVisible(){ return this.Cf }
 *
 * `Cf` means MOUNTED, not "panel open". So `show()` only mounts the widget, and
 * is a no-op once the launcher bubble is already on the page — which on our
 * surfaces it always is, because remote config auto-mounts it. The manager
 * exposes no `open()`, and the extension registers no event we could dispatch
 * (its only `addEventListener` is for `online`). Clicking its own launcher is
 * the sole way in.
 *
 * That couples us to third-party markup, so it is written to fail toward the
 * mailto rather than toward silence: every branch that cannot prove it opened
 * the panel returns `false`, and the caller then lets the `mailto:` href
 * proceed. If PostHog renames the container, this degrades to email instead of
 * becoming a button that does nothing — which is exactly what shipped the
 * first time.
 */
function openSupportChat(): boolean {
	if (typeof document === "undefined") return false;

	const container = document.getElementById(WIDGET_CONTAINER_ID);
	if (!container) return false;

	// Panel already open: clicking the launcher again would CLOSE it. The user
	// asked to reach support and support is already reachable, so this counts
	// as opened and must not fall through to the mail client.
	if (container.querySelector("textarea")) return true;

	const launcher = container.querySelector("button");
	if (!launcher) return false;

	launcher.click();
	return true;
}

export interface SupportLauncherProps {
	surface: SupportSurface;
	/** Prefills the mailto subject on the fallback path. */
	mailtoSubject?: string;
	className?: string;
	children: ReactNode;
	/**
	 * Fires after `support_widget_opened` capture, with whichever channel was
	 * actually reached — lets a caller layer its OWN higher-signal event (e.g.
	 * the B2B inbound-contact CTA's `b2b_contact_opened`) on top of this
	 * launcher's generic capture without duplicating the open-detection logic.
	 * Never throws into the click handler.
	 */
	onOpened?: (channel: "chat" | "mailto") => void;
}

/**
 * Opens the PostHog Support chat when it is loaded, and falls back to the
 * support mailto when it is not.
 *
 * The fallback is not an error path — it is the normal path for a large slice
 * of real traffic, so this renders as a plain `<a href="mailto:…">` and only
 * *upgrades* the click when chat is genuinely available:
 *
 *   - EU/EEA/UK/CH visitors who never consented: `PostHogProvider` never calls
 *     `posthog.init()` for them at all, so there is no widget to open.
 *   - Ad-blocked visitors: the conversations script never loads.
 *   - Apex soft navigations: `disable_conversations` is fixed at init (see
 *     `shouldEnableSupportWidget`), so someone who landed on the feed and then
 *     navigated here in-app has no widget on this page load.
 *
 * Because the anchor is the base state, the control still works with JS
 * disabled and screen readers announce a real link destination.
 */
export function SupportLauncher({
	surface,
	mailtoSubject,
	className,
	children,
	onOpened,
}: SupportLauncherProps) {
	const posthog = usePostHog();

	const mailtoHref = mailtoSubject
		? `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(mailtoSubject)}`
		: `mailto:${SUPPORT_EMAIL}`;

	const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
		const openedChat = openSupportChat();

		if (openedChat) event.preventDefault();

		const channel = openedChat ? "chat" : "mailto";
		try {
			posthog?.capture?.("support_widget_opened", {
				surface,
				channel,
			});
		} catch {
			// Telemetry must never block someone trying to reach support.
		}

		try {
			onOpened?.(channel);
		} catch {
			// Same contract — a caller's telemetry must never block this click.
		}
	};

	return (
		<a
			href={mailtoHref}
			onClick={handleClick}
			className={className ?? undefined}
		>
			{children}
		</a>
	);
}

/** Shared primary-button styling for the launcher's default placements. */
export const SUPPORT_LAUNCHER_BUTTON_CLASS = `inline-flex items-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 ${FOCUS_RING}`;
